import React, { useState, useEffect, useCallback } from 'react';
import { SignaturePad } from '../components/SignaturePad';
import { signatureService } from '../services/api';
import { useAuth } from '../context/AuthContext';

export const SignaturePage: React.FC = () => {
  const { user } = useAuth();
  const [currentSignature, setCurrentSignature] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchSignature = useCallback(async () => {
    setLoading(true);
    try {
      const response = await signatureService.getMySignature();
      setCurrentSignature(response.data.signature);
    } catch (err: any) {
      // No signature exists yet, which is fine
      setCurrentSignature(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSignature();
  }, [fetchSignature]);

  const handleSignatureCapture = async (signature: string) => {
    setMessage('Signature saved successfully!');
    setCurrentSignature(signature);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDeleteSignature = async () => {
    if (!window.confirm('Are you sure you want to delete your signature?')) return;

    try {
      await signatureService.deleteSignature();
      setCurrentSignature(null);
      setMessage('Signature deleted successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError('Failed to delete signature');
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500 font-sans">Loading Signature Data...</div>;

  const canManage = ['super_admin', 'state_manager'].includes(user?.role || '');

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto font-sans min-w-0 max-w-full overflow-hidden">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-2.5 sm:gap-3">
          <span>Signature Management</span>
          <button onClick={fetchSignature} className="p-1.5 sm:p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full transition-colors border border-slate-700" title="Refresh Data">
            <svg className="w-3.5 sm:w-4 h-3.5 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
        </h1>
        <p className="text-slate-400 text-sm">Manage your signature for approving reports and verifying assets.</p>
      </div>

      {!canManage && (
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl">
          <p className="font-bold">Restricted Access</p>
          <p className="text-sm opacity-90 mt-1">Only admins and managers can manage signatures.</p>
        </div>
      )}

      {message && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 text-green-400 rounded-xl">{message}</div>
      )}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-8">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
            <h2 className="text-xl font-bold text-white mb-4">Your Signature</h2>
            
            {currentSignature ? (
              <div className="space-y-4">
                <div className="border border-slate-700 rounded-xl p-4 bg-slate-800/50 flex justify-center">
                  <img
                    src={currentSignature}
                    alt="Your signature"
                    className="w-full h-auto max-h-40 object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.2)]"
                  />
                </div>
                <button
                  onClick={handleDeleteSignature}
                  disabled={!canManage}
                  className="w-full px-4 py-3 bg-red-500/10 border border-red-500/30 text-red-400 font-bold rounded-xl hover:bg-red-500/20 disabled:opacity-50 transition"
                >
                  Delete Signature
                </button>
              </div>
            ) : (
              <div className="text-center py-10 border border-dashed border-slate-700 rounded-xl bg-slate-900/50">
                <p className="text-slate-500">No signature set yet.</p>
                <p className="text-xs text-slate-600 mt-2">Create one using the pad to the right.</p>
              </div>
            )}
          </div>

          <div className="bg-orange-500/5 border border-orange-500/20 rounded-2xl p-6">
            <h3 className="font-bold text-orange-400 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              About Signatures
            </h3>
            <ul className="text-sm text-orange-200/70 space-y-2 mt-4">
              <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> Your signature will be used when approving official reports.</li>
              <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> Only admins and managers can upload signatures.</li>
              <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> Signatures are stored securely and associated permanently with your ID.</li>
              <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> You can update or replace your signature at any time.</li>
            </ul>
          </div>
        </div>

        {canManage && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl sticky top-6 self-start">
            <h2 className="text-xl font-bold text-white mb-6">
              {currentSignature ? 'Update Signature' : 'Create Signature'}
            </h2>
            <div className="bg-white rounded-xl overflow-hidden p-2 border-2 border-slate-700 focus-within:border-orange-500 transition-colors">
              <SignaturePad onSignatureCapture={handleSignatureCapture} />
            </div>
            <p className="text-xs text-slate-500 mt-4 text-center">Please sign clearly within the dotted box above.</p>
          </div>
        )}
      </div>
    </div>
  );
};
